<script setup lang="ts">
import { computed, onMounted, ref, toRefs } from 'vue';
import AutoComplete from 'primevue/autocomplete';
import { DropdownChangeEvent } from 'primevue/dropdown';
import useVuelidate from '@vuelidate/core';
import { required, requiredIf, url, helpers } from '@vuelidate/validators';
import { formatPhone, validatePhone } from '@/composables';
import { useCitiesStore, useCountriesStore, useLocationTypesStore, useStatesStore } from '@/store';
import { LocationFormInterface } from '@/types';
import { EMIT } from '@/enum';

interface PropsInterface {
  data: LocationFormInterface;
  loading: boolean;
  isOrganization: boolean;
  isTenant?: boolean;
  permissionToEdit?: boolean;
}

const props = defineProps<PropsInterface>();
const zipcode = helpers.regex(/^\d{5}(-\d{4})?$/);

/**
 * Define all the available emit events.
 */
const emit = defineEmits<{
  (e: EMIT.SAVE, value: LocationFormInterface): void;
}>();

/**
 * Convert the properties into references.
 */
const { data, loading, isOrganization, isTenant } = toRefs(props);

/**
 * The suggested cities after running a search.
 */
const cities = ref([]);

/**
 * The available states before and after selecting a country.
 */
const states = ref([]);

/**
 * The location reference object to contain the form data.
 */
const location = ref({ ...data.value });

/**
 * All of the state references including the convertion of the
 * location state object into a ref.
 */
const citiesStore = useCitiesStore();
const countriesStore = useCountriesStore();
const locationTypesStore = useLocationTypesStore();
const statesStore = useStatesStore();

/**
 * The required validation rules.
 */
const rules = {
  location_type_id: { requiredIfCustom: requiredIf(isOrganization.value) },
  country_id: { required },
  address: { required },
  city_name: { required },
  state_id: { required },
  zipcode: { required, zipcode: helpers.withMessage('Zipcode must be valid', zipcode) },
  phone: { required, validatePhone },
  website: { requiredIfCustom: requiredIf(isOrganization.value), url },
};

/**
 * Initialize the vuelidate instance.
 */
const v$ = useVuelidate(rules, location);

/**
 * Throw all errors here for easier access.
 */
const errors = ref(null);

/**
 * Initialize everything required to build out the component.
 */
onMounted(() => init());

/**
 * Check if all component resources have completed loading.
 */
const isLoading = computed(() => {
  return (
    loading.value || citiesStore.loading || countriesStore.loading || locationTypesStore.loading || statesStore.loading
  );
});

/**
 * Define the request update payload.
 */
const payload = computed(() => {
  const value = location.value;

  return {
    location_type_id: value.location_type_id,
    country_id: value.country_id,
    address: value.address,
    city_id: value.city_id,
    city_name: value.city_name,
    state_id: value.state_id,
    zipcode: value.zipcode,
    phone: value.phone,
    website: value.website,
  };
});

/**
 * Initialize the location types, countries, states and location.
 */
const init = async () => {
  await locationTypesStore.getLocationTypes();
  await countriesStore.getCountries();
  await statesStore.getStates();
  states.value = statesStore.states;
  onCancel();
};

/**
 * Return a cloned version of the state location object.
 */
const clonedLocation = () => {
  return { ...data.value };
};

/**
 * Reset the vuelidate dirty status for all keys.
 */
const resetDirty = () => v$.value.$reset();

/**
 * Get the field styles based on the specific key.
 */
const fieldStyles = (key: string, styles?: string): string => {
  const validation =
    (v$.value[key].$invalid && v$.value[key].$dirty) || (errors.value && errors.value[key]) ? 'p-invalid' : '';
  return `${styles} ${validation}`;
};

/**
 * Handle the cancellation of all changes made to the location
 * by restting the values and statuses.
 */
const onCancel = () => {
  location.value = clonedLocation();
  errors.value = null;
  resetDirty();
};

/**
 * Handle the client side validation, followed by saving of all changes made
 * to the location.
 */
const onSave = async () => {
  // Don't allow a submit event unless the fields are valid.
  if (v$.value.$invalid) return;

  emit(EMIT.SAVE, payload.value);
  errors.value = null;
  resetDirty();
};

/**
 * Search for a city with the provided city name, state and country identifier.
 * Also, Attempt to pre-populate the state based on the provided city name.
 */
const onCitySearch = async () => {
  if (location.value.country_id && location.value.state_id && location.value.city_name) {
    const params = {
      country_id: location.value.country_id,
      search: location.value.city_name,
    };

    citiesStore.searchCities(params).then((response) => (cities.value = response.data.data));
  } else {
    const response = await citiesStore.searchCities({ search: location.value.city_name });
    const stateIds = response.data.data.map((item) => item.state_id);

    if (stateIds.length === 1) location.value.state_id = stateIds[0];
  }
};

/**
 * Update the city, state and country identifiers once a suggested
 * city has been selected.
 */
const onCityUpdate = (event) => {
  location.value.city_id = event.value.id;
  location.value.city_name = event.value.name;
  location.value.state_id = event.value.state_id;
  location.value.country_id = event.value.country_id;
};

/**
 * Update the listing of states based on the selected country.
 */
const onCountryChange = (event: DropdownChangeEvent) =>
  (states.value = statesStore.states.filter((item) => item.country_id === event.value));

/**
 * Format the phone number when the value has changed.
 */
const onPhone = () => (location.value.phone = formatPhone(location.value.phone as string));
</script>

<template>
  <FormCard
    title="Location"
    :dirty="v$.$anyDirty && permissionToEdit"
    :cancel="onCancel"
    :save="onSave"
    :save-disabled="v$.$anyDirty && v$.$invalid"
  >
    <template #form>
      <form id="location_form" class="w-full">
        <div class="formgrid grid">
          <div v-if="isOrganization" class="field col pr-3">
            <label for="location_type" class="requried">Location Type</label>
            <Skeleton v-if="isLoading" height="2.3rem" />
            <Dropdown
              v-else
              id=" location_type"
              v-model="v$.location_type_id.$model"
              :options="locationTypesStore.types"
              option-label="title"
              option-value="id"
              :loading="isLoading"
              placeholder="Select a Type"
              :class="fieldStyles('location_type_id')"
              :disabled="!permissionToEdit"
            />
          </div>
          <div class="field col">
            <label for="country" class="required">Country</label>
            <Skeleton v-if="isLoading" height="2.3rem" />
            <Dropdown
              v-else
              id="location_country_id"
              v-model="v$.country_id.$model"
              :options="countriesStore.countries"
              option-label="name"
              option-value="id"
              :loading="isLoading"
              placeholder="Select a Country"
              :class="fieldStyles('country_id')"
              :disabled="!permissionToEdit"
              @change="onCountryChange"
            />
          </div>
        </div>
        <div class="field">
          <label for="address" class="required">Address</label>
          <Skeleton v-if="isLoading" height="2.3rem" />
          <InputText
            v-else
            id="location_address"
            v-model="v$.address.$model"
            type="text"
            :class="fieldStyles('address', 'w-full')"
            placeholder="Address"
            :disabled="!permissionToEdit"
          />
          <div v-for="error of v$.address.$errors" :key="error.$uid" class="input-errors">
            <small>{{ error.$message }}</small>
          </div>
        </div>
        <div class="formgrid grid">
          <div class="field col">
            <label for="city" class="required">City</label>
            <Skeleton v-if="isLoading" height="2.3rem" />
            <AutoComplete
              v-else
              id="location_city"
              v-model="v$.city_name.$model"
              :class="fieldStyles('city_name')"
              field="name"
              :min-length="2"
              :suggestions="cities"
              :disabled="!permissionToEdit"
              @complete="onCitySearch"
              @item-select="onCityUpdate"
            />
            <div v-for="error of v$.city_name.$errors" :key="error.$uid" class="input-errors">
              <small>{{ error.$message }}</small>
            </div>
          </div>
          <div class="field col">
            <label for="state" class="required">State</label>
            <Skeleton v-if="isLoading" height="2.3rem" />
            <Dropdown
              v-else
              id="location_state_id"
              v-model="v$.state_id.$model"
              :options="states"
              option-label="name"
              option-value="id"
              :loading="isLoading"
              placeholder="Select a State"
              :disabled="!permissionToEdit"
              :class="fieldStyles('state_id', 'w-full')"
            />
          </div>
          <div class="field col">
            <label for="zipcode" class="required">Zipcode</label>
            <Skeleton v-if="isLoading" height="2.3rem" />
            <InputText
              v-else
              id="location_zipcode"
              v-model="v$.zipcode.$model"
              type="text"
              :class="fieldStyles('zipcode')"
              :disabled="!permissionToEdit"
              placeholder="Zipcode"
            />
            <div v-for="error of v$.zipcode.$errors" :key="error.$uid" class="input-errors">
              <small>{{ error.$message }}</small>
            </div>
          </div>
        </div>
        <div class="field">
          <label for="phone" :class="{ required: isOrganization }">Phone</label>
          <Skeleton v-if="isLoading" height="2.3rem" />
          <InputText
            v-else
            id="location_phone"
            v-model="v$.phone.$model"
            type="text"
            :class="fieldStyles('phone')"
            maxlength="12"
            :disabled="!permissionToEdit"
            placeholder="999-999-9999"
            @input="onPhone"
          />
          <div v-for="error of v$.phone.$errors" :key="error.$uid" class="input-errors">
            <small>{{ error.$message }}</small>
          </div>
        </div>
        <div v-if="isOrganization || isTenant">
          <label for="website" :class="{ required: isOrganization }">Website</label>
          <Skeleton v-if="isLoading" height="2.3rem" />
          <InputText
            v-else
            id="location_website"
            v-model="v$.website.$model"
            type="text"
            :disabled="!permissionToEdit"
            :class="fieldStyles('website')"
            placeholder="Website"
          />
          <div v-for="error of v$.website.$errors" :key="error.$uid" class="input-errors">
            <small>{{ error.$message }}</small>
          </div>
        </div>
      </form>
    </template>
  </FormCard>
</template>
